import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para detectar leitura de código de barras
 * Leitores de código de barras funcionam como teclados - enviam caracteres muito rapidamente seguidos de Enter
 * 
 * @param {Object} options
 * @param {boolean} options.enabled - Se o scanner está ativo
 * @param {Function} options.onScan - Callback quando um código é lido
 * @param {number} options.minLength - Tamanho mínimo do código (default: 4)
 * @param {number} options.maxDelay - Delay máximo entre caracteres em ms (default: 150)
 */
const useBarcodeScanner = ({ 
    enabled = true, 
    onScan, 
    minLength = 4, 
    maxDelay = 150 
}) => {
    const [isScanning, setIsScanning] = useState(false);
    const bufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const timeoutRef = useRef(null);

    const resetBuffer = useCallback(() => {
        bufferRef.current = '';
        setIsScanning(false);
        lastKeyTimeRef.current = 0;
    }, []);

    const processBarcode = useCallback((code) => {
        console.log('[SCANNER] Processando código:', code, '(length:', code.length + ')');
        if (onScan && code.length >= minLength) {
            onScan(code);
        }
        resetBuffer();
    }, [onScan, minLength, resetBuffer]);

    const handleKeyDown = useCallback((event) => {
        if (!enabled) return;

        const now = Date.now();
        const timeSinceLastKey = lastKeyTimeRef.current > 0 ? (now - lastKeyTimeRef.current) : 0;
        const key = event.key;

        // Enter - finalizar leitura
        if (key === 'Enter') {
            if (bufferRef.current.length >= minLength) {
                event.preventDefault();
                event.stopPropagation();
                
                const scannedCode = bufferRef.current;
                console.log('[SCANNER] Enter detectado - Código completo:', scannedCode);
                
                // Limpar timeout se existir
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                
                processBarcode(scannedCode);
                return;
            }
            // Enter sem código suficiente - resetar
            if (bufferRef.current.length > 0) {
                console.log('[SCANNER] Enter com código curto, descartando:', bufferRef.current);
            }
            resetBuffer();
            return;
        }

        // Aceitar números e letras para código de barras
        // Códigos EAN/UPC são numéricos, mas alguns scanners podem enviar letras como prefixo
        if (key.length === 1 && /^[a-zA-Z0-9]$/.test(key)) {
            
            // Se passou muito tempo desde a última tecla e já tem buffer, resetar
            if (timeSinceLastKey > maxDelay && bufferRef.current.length > 0) {
                console.log('[SCANNER] Timeout entre teclas (' + timeSinceLastKey + 'ms) - resetando buffer:', bufferRef.current);
                resetBuffer();
            }
            
            // Adicionar caractere ao buffer
            bufferRef.current += key;
            lastKeyTimeRef.current = now;
            setIsScanning(true);

            console.log('[SCANNER] Tecla:', key, '| Buffer:', bufferRef.current, '| Delay:', timeSinceLastKey + 'ms');

            // Limpar timeout anterior
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Criar novo timeout - se não receber mais caracteres, processar o que tem
            timeoutRef.current = setTimeout(() => {
                if (bufferRef.current.length >= minLength) {
                    console.log('[SCANNER] Timeout final - processando código:', bufferRef.current);
                    processBarcode(bufferRef.current);
                } else {
                    console.log('[SCANNER] Timeout final - código muito curto (' + bufferRef.current.length + '), descartando:', bufferRef.current);
                    resetBuffer();
                }
            }, maxDelay * 3);
        }
        // Ignorar outras teclas (Shift, Ctrl, etc.) - não resetar o buffer
    }, [enabled, maxDelay, minLength, processBarcode, resetBuffer]);

    useEffect(() => {
        if (enabled) {
            // Usar capture phase para pegar eventos antes dos inputs
            document.addEventListener('keydown', handleKeyDown, true);
            console.log('[SCANNER] ✅ Scanner ATIVADO - minLength:', minLength, 'maxDelay:', maxDelay + 'ms');
        } else {
            document.removeEventListener('keydown', handleKeyDown, true);
            console.log('[SCANNER] ⛔ Scanner DESATIVADO');
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [enabled, handleKeyDown, minLength, maxDelay]);

    return {
        isScanning,
        resetBuffer
    };
};

export default useBarcodeScanner;
